import React, { Component } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { connect } from 'react-redux';
import {
  clearNoticesReducer,
  createNotificationWithFilters,
  getNotificationFilterOptions,
  getNotificationReceivers,
  getNotificationTargets,
} from 'actions/Notices';
import {
  DATE_FORMAT,
  DIVERSION_POLICIES,
  NOTICES,
  NOTICE_BODY_MAX_LENGTH,
  TEXT_TITLE_MAX_LENGTH,
} from 'constants/Values';
import moment from 'moment';
import { Button } from 'components/Button';
import LoadableSplashScreen from 'components/LoadableSplashScreen';
import AddIcon from '@mui/icons-material/Add';
import { Filter } from 'components/Notifications';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { differenceBy, isEqual } from 'lodash';
import { List } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import Targets from 'components/Notifications/Targets';
import { injectIntl } from 'react-intl';
import { SimpleText } from 'util/IntlMessages';
import { showErrorNotification } from 'actions';

import DiversionSubform from 'components/Notifications/DiversionSubform';
import { diversionBodyBudget } from 'components/Notifications/diversionBody';
import MessageEditor from 'components/MessageEditor';
import DestructiveConfirmationDialog from 'components/dialogs/DestructiveConfirmationDialog';
import { bodyToSubformState } from 'util/diversion';
import { FOCUS_DIVERSIONS_TAB_REQUEST } from 'constants/ActionTypes';
import styles from './NotificationForm.module.scss';

// Task 03 will export this from 'actions/Diversion'. Stub inline per Ambiguity #8
// default — task 03 will absorb the duplicate when it merges.
// Task 11 wires the consumer (auto-focus on Diversions tab).
const focusDiversionsTabRequest = () => ({ type: FOCUS_DIVERSIONS_TAB_REQUEST });

const noticeTypes = [
  NOTICES.TYPES.GENERAL,
  NOTICES.TYPES.DELAY,
  NOTICES.TYPES.SECURITY,
  NOTICES.TYPES.DIVERSION,
];

function convertTimeInputs(date, time) {
  return moment(`${date}T${time}`).format(DATE_FORMAT.SERVER_DATE_TIME);
}

class NotificationForm extends Component {
  static propTypes = {
    intl: object,
    showErrorNotification: func.isRequired,
    clearNoticesReducer: func.isRequired,
    getNotificationTargets: func.isRequired,
    getNotificationFilterOptions: func.isRequired,
    createNotificationWithFilters: func.isRequired,
    getNotificationReceivers: func.isRequired,
    // Seeded into state via the static templateSeed/templateIdentity helpers
    // (constructor state initializer + componentDidUpdate), and read directly
    // as this.props.template there too.
    template: shape({
      body: string,
      header: string,
      type: string,
    }),
    routes: array,
    toRoute: bool,
    allReceivers: array,
    allFilters: array,
    targets: array,
    targetsLoading: bool,
    userRoles: array,
    focusDiversionsTabRequest: func,
    // Prefilled by task 09's RouteDetails banner Cancel button via
    // showSendNoticeModal payload → modal reducer → SendNoticeDialog. When
    // defined AND template.type === 'DIVERSION', NotificationForm.templateSeed
    // copies it into state.diversionNotificationId for DiversionSubform.serializeBody
    // to thread into the cancel YAML. Consumed via the static templateSeed/
    // templateIdentity helpers (constructor state initializer + componentDidUpdate),
    // and read directly as this.props.prefilledDiversionNotificationId there too.
    prefilledDiversionNotificationId: string,
  };

  static defaultProps = {
    routes: [],
    template: {
      body: '',
      header: '',
      type: '',
    },
    toRoute: false,
    prefilledDiversionNotificationId: undefined,
  };

  static templateSeed(template, prefilledDiversionNotificationId) {
    if (
      !template ||
      template.body === undefined ||
      template.header === undefined ||
      !template.type
    ) {
      return null;
    }
    const accepted = noticeTypes.indexOf(template.type) > -1 ? template.type : '';
    const updates = { body: template.body, header: template.header, type: accepted };
    if (accepted === NOTICES.TYPES.DIVERSION) {
      updates.diversion = bodyToSubformState(template.body);
      updates.diversionValidationErrors = {};
      if (prefilledDiversionNotificationId !== undefined) {
        updates.diversionNotificationId = prefilledDiversionNotificationId;
      }
    }
    return updates;
  }

  static templateIdentity(props) {
    // prefilled-diversion cancel flow keys on its notificationId; template
    // selection keys on the template's id. Scalars only (stable) — never the object.
    if (props.prefilledDiversionNotificationId !== undefined) {
      return `diversion:${props.prefilledDiversionNotificationId}`;
    }
    return props.template && props.template.id !== undefined
      ? `template:${props.template.id}`
      : null;
  }

  state = {
    type: NOTICES.TYPES.GENERAL, // DELAY, SECURITY, or GENERAL
    receivers: [],
    filters: this.props.toRoute
      ? [
          {
            name: 'FROM_OVERVIEW',
            options: this.props.routes.map(route => route.id),
          },
        ]
      : [],
    exclusions: [],
    header: '',
    body: '',
    schedule: false,
    date: '',
    time: '',
    expiring: false,
    expDate: '',
    expTime: '',
    filterDialogOpen: false,
    targetsDialogOpen: false,
    targetCountsRequest: null,
    targetCounts: null,
    validationMessages: [],

    // DIVERSION-specific state. Lifted-state for the subform: value flows down
    // through <MessageEditor>, changes flow up via handleMessageEditorChange.
    // notificationId is the threaded prop for the banner-prefilled cancel
    // composer (undefined here; the cancel-from-banner flow wires the prefill).
    diversion: {
      preamble: '',
      shelters: [],
      policy: DIVERSION_POLICIES.NEAREST, // default per spec D2
      cancel: false,
    },
    diversionNotificationId: undefined,
    diversionValidationErrors: {}, // { preamble?: string, shelters?: string }

    // Send-confirmation modal (DIVERSION-only, fires for both create and cancel).
    confirmModalOpen: false,
    confirmModalVariant: 'create', // 'create' | 'cancel' — drives the body-copy variant
    seededTemplateIdentity: NotificationForm.templateIdentity(this.props),
    ...NotificationForm.templateSeed(
      this.props.template,
      this.props.prefilledDiversionNotificationId,
    ),
  };

  componentDidMount() {
    this.props.getNotificationReceivers();
  }

  // eslint-disable-next-line no-unused-vars
  componentDidUpdate(prevProps, prevState, snapshot) {
    const { allReceivers, targets } = this.props;
    const { targetCounts } = this.state;
    if (allReceivers && !targetCounts) {
      this.updateCounts();
    }
    if (prevProps.targets !== targets) {
      this.updateCounts();
    }

    const identity = NotificationForm.templateIdentity(this.props);
    if (identity && identity !== this.state.seededTemplateIdentity) {
      const seed = NotificationForm.templateSeed(
        this.props.template,
        this.props.prefilledDiversionNotificationId,
      );
      if (seed) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ ...seed, seededTemplateIdentity: identity });
      }
    }
  }
  componentWillUnmount() {
    this.props.clearNoticesReducer();
  }

  getNotificationTargetsRequest = () => {
    const { receivers, filters, boardedOnly } = this.state;
    const notificationRequest = {
      targets: {
        filters,
        receivers,
      },
    };

    const swapOverviewFilter = this.props.toRoute && boardedOnly;
    if (swapOverviewFilter) {
      notificationRequest.targets.filters = notificationRequest.targets.filters.map(f => {
        if (f.name === 'FROM_OVERVIEW') {
          return {
            ...f,
            name: 'ON_BOARD_BUS',
          };
        }
        return f;
      });
    }

    return notificationRequest;
  };

  /**
   * Driver-count for the send-confirm modal copy. Returns null when the targets
   * cache is empty or stale (in which case the modal renders the "without-count"
   * variant per spec PI-1). Driver-count is the count of resolved targets whose
   * receiverType is 'DRIVER' (the spec's "drivers" wording maps to the DRIVER
   * receiver enum — verified in TargetsTableDef.js field shape).
   */
  getDriverCount = () => {
    const { targets } = this.props;
    if (!targets || targets.length === 0) return null;
    if (this.hasFiltersChanged()) return null; // counts are stale
    return targets.filter(t => t.receiverType && t.receiverType.value === 'DRIVER').length;
  };

  /**
   * Pick the i18n key + interpolation values for the send-confirm modal body
   * based on (variant, count availability).
   */
  getSendConfirmBodyKey = variant => {
    const count = this.getDriverCount();
    if (count != null && count > 0) {
      return {
        id:
          variant === 'cancel'
            ? 'diversion.send-confirm.body.cancel.with-count'
            : 'diversion.send-confirm.body.create.with-count',
        values: { count },
      };
    }
    return {
      id:
        variant === 'cancel'
          ? 'diversion.send-confirm.body.cancel.without-count'
          : 'diversion.send-confirm.body.create.without-count',
      values: undefined,
    };
  };

  /**
   * Lifted-state callback from <MessageEditor>. Receives the full next
   * { header, body, type, diversion } object and merges it into local state.
   *
   * Side effects:
   *   - If type switches TO DIVERSION, also clears scheduling state
   *     (composer-owned fields that <MessageEditor> doesn't know about).
   *   - If diversion changed (compared by reference), clears
   *     diversionValidationErrors (replicates handleDiversionChange semantics).
   *   - If type switches AWAY FROM DIVERSION, resets diversionNotificationId.
   */
  handleMessageEditorChange = nextContent => {
    const { type: prevType, diversion: prevDiversion } = this.state;
    const updates = {
      type: nextContent.type,
      header: nextContent.header,
      body: nextContent.body,
      diversion: nextContent.diversion,
    };

    // Clear scheduling when entering DIVERSION (Diversions send immediately).
    if (nextContent.type === NOTICES.TYPES.DIVERSION && prevType !== NOTICES.TYPES.DIVERSION) {
      updates.schedule = false;
      updates.expiring = false;
      updates.date = '';
      updates.time = '';
      updates.expDate = '';
      updates.expTime = '';
    }

    // Clear diversionValidationErrors whenever diversion changes.
    if (nextContent.diversion !== prevDiversion) {
      updates.diversionValidationErrors = {};
    }

    // Reset diversionNotificationId when switching away from DIVERSION.
    if (prevType === NOTICES.TYPES.DIVERSION && nextContent.type !== NOTICES.TYPES.DIVERSION) {
      updates.diversionNotificationId = undefined;
    }

    this.setState(updates);
  };

  handleFormChange = field => event => {
    this.setState({ [field]: event.target.value });
  };

  handleReceiversChange = event => {
    const { validationMessages } = this.state;
    const { value, checked } = event.target;
    this.setState(
      prevState => ({
        receivers: checked
          ? [...prevState.receivers, value]
          : prevState.receivers.filter(item => item !== value),
      }),
      () => {
        if (validationMessages.length) {
          this.validateFilters();
        }
        this.updateCounts();
      },
    );
  };

  handleCheckboxChange = field => () => {
    this.setState(
      prevState => ({ [field]: !prevState[field] }),
      field === 'boardedOnly' ? this.updateCounts : undefined,
    );
  };

  handleAddFilter = filter => {
    const { name } = filter;
    this.clearAllValidations();
    this.setState(
      prevState => ({
        filters: [
          ...prevState.filters,
          {
            name,
            options: [],
          },
        ],
        filterDialogOpen: false,
      }),
      this.updateFilterState,
    );
  };

  handleFilterDelete = filterName => {
    this.clearValidation({ name: filterName });
    this.setState(
      prevState => ({
        filters: prevState.filters.filter(filter => filter.name !== filterName),
      }),
      this.updateFilterState,
    );
  };

  handleFilterOptionsChange = (filterName, selectedValues) => {
    const { validationMessages } = this.state;
    this.setState(
      prevState => ({
        filters: prevState.filters.map(
          filter =>
            filter.name === filterName
              ? {
                  ...filter,
                  options: selectedValues,
                }
              : filter,
        ),
      }),
      () => {
        if (validationMessages.length) {
          this.validateFilters();
        }
        this.updateFilterState();
      },
    );
  };

  updateFilterState = () => {
    const { filters } = this.state;
    const { allReceivers } = this.props;

    this.props.getNotificationFilterOptions({
      receivers: allReceivers.map(r => r.value),
      filters,
    });
    this.updateCounts();
  };

  updateCounts = () => {
    const { allReceivers } = this.props;
    if (!allReceivers) {
      return;
    }
    if (this.hasFiltersChanged()) {
      const count = (
        <Box component="span" style={{ opacity: 0.3 }} sx={{ fontSize: '80%', fontWeight: 400 }}>
          <SimpleText id="notifications.send-form.button.refresh-summary" />
        </Box>
      );
      this.setState({ targetCounts: allReceivers.map(({ value }) => ({ value, count })) });
    } else {
      const { targets } = this.props;
      const { exclusions } = this.state;
      const targetCounts = allReceivers.reduce((summary, receiver) => {
        const matchingTargets = (targets || []).filter(
          target => target.receiverType.value === receiver.value,
        );
        const count = matchingTargets.length;
        const exclusionCount = matchingTargets.filter(target => exclusions.includes(target.id))
          .length;
        summary.push({
          value: receiver.value,
          count: `${count - exclusionCount} (${exclusionCount})`,
        });

        return summary;
      }, []);
      this.setState({ targetCounts });
    }
  };

  /**
   * Send button handler. For DIVERSION:
   *   1. Run the standard filter validation (existing path).
   *   2. Run DiversionSubform.validate(diversion) — block + surface inline errors if !ok.
   *   3. Open the send-confirm modal — actual dispatch happens in handleConfirmSend.
   * For all other types: dispatch directly (existing behaviour).
   */
  handleSend = () => {
    if (!this.validateFilters(true, true)) return;

    const { type, diversion } = this.state;

    if (type === NOTICES.TYPES.DIVERSION) {
      const validation = DiversionSubform.validate(diversion);
      if (!validation.ok) {
        this.setState({ diversionValidationErrors: validation.errors });
        // No toast — the inline error renders alongside the subform fields.
        return;
      }
      // Open the send-confirm modal. Variant is 'cancel' if the cancel toggle
      // is on, else 'create'.
      this.setState({
        confirmModalOpen: true,
        confirmModalVariant: diversion.cancel ? 'cancel' : 'create',
        diversionValidationErrors: {},
      });
      return;
    }

    // Non-DIVERSION path — unchanged from the original handleSend.
    this.dispatchNotification();
  };

  /**
   * Compose the notification request and dispatch. Extracted so both the
   * non-DIVERSION direct-send path and the DIVERSION post-confirm path share
   * the dispatch shape.
   *
   * For DIVERSION, body is composed via DiversionSubform.serializeBody(...) and
   * delayed/expires are forced to null (spec: "Diversions send immediately").
   */
  dispatchNotification = () => {
    const {
      type,
      exclusions,
      header,
      body,
      schedule,
      date,
      time,
      expiring,
      expDate,
      expTime,
      diversion,
      diversionNotificationId,
    } = this.state;

    let templateBody = body;
    let templateDelayed = schedule ? convertTimeInputs(date, time) : null;
    let templateExpires = expiring ? convertTimeInputs(expDate, expTime) : null;

    if (type === NOTICES.TYPES.DIVERSION) {
      templateBody = DiversionSubform.serializeBody(diversion, diversionNotificationId);
      // Diversions send immediately — see spec D2 + Cross-cutting rules.
      templateDelayed = null;
      templateExpires = null;
    }

    const notificationRequest = {
      ...this.getNotificationTargetsRequest(),
      template: {
        header,
        body: templateBody,
        type,
        delayed: templateDelayed,
        expires: templateExpires,
      },
      excluding: exclusions,
    };

    this.props.createNotificationWithFilters(notificationRequest, !this.props.toRoute);
  };

  handleConfirmSend = () => {
    this.setState({ confirmModalOpen: false });
    this.dispatchNotification();
    // Task 11 success-handler hook — placeholder dispatch. Task 11 wires the
    // consumer (auto-focus on Diversions tab). Until task 11 merges this is a
    // no-op dispatch; the saga registers no handler so nothing breaks.
    this.props.focusDiversionsTabRequest();
  };

  handleCancelSend = () => {
    this.setState({ confirmModalOpen: false });
    // State is intact — TM is back at the form. Spec D2: "returns to form with state intact."
  };

  isSubmitingDisabled = () => {
    const { type, body, header, receivers, diversion, diversionNotificationId } = this.state;
    if (!type || !receivers.length) return true;
    // DIVERSION: body/header are not required in state — the subform provides the body and
    // header is optional (spec D2: preamble is the body). It is NOT unbounded though, and
    // returning false here meant the length check below never ran on the one type whose body
    // the operator cannot see in full: the safe zones are serialised into it, so a
    // shelter-heavy diversion overran the server's 1000 and 400'd at send time with nothing
    // on screen to explain it. Measure the string that actually goes on the wire.
    if (type === NOTICES.TYPES.DIVERSION) {
      return diversionBodyBudget(diversion, diversionNotificationId).over;
    }
    return (
      !body ||
      body.length > NOTICE_BODY_MAX_LENGTH ||
      !header ||
      header.length > TEXT_TITLE_MAX_LENGTH
    );
  };

  handleFilterDialogOpen = () => {
    if (!this.props.allFilters || !this.props.allFilters.length) this.updateFilterState();
    this.setState({ filterDialogOpen: true });
  };

  handleFilterDialogClose = () => {
    this.setState({ filterDialogOpen: false });
  };

  handleTargetsDialogOpen = () => {
    if (this.validateFilters(true)) {
      if (this.hasFiltersChanged()) {
        this.refreshTargets();
      }
      this.setState({
        targetsDialogOpen: true,
      });
    }
  };

  handleRefreshTargets = () => {
    if (this.validateFilters(true)) {
      this.refreshTargets();
    }
  };

  refreshTargets = () => {
    const { exclusions } = this.state;
    const notificationRequest = this.getNotificationTargetsRequest();
    this.props.getNotificationTargets(notificationRequest.targets);
    this.setState({
      targetCountsRequest: {
        ...notificationRequest,
        exclusions,
      },
    });
  };

  handleTargetsDialogSubmit = exclusions => {
    this.setState(
      prevState => ({
        exclusions,
        targetCountsRequest: { ...prevState.targetCountsRequest, exclusions },
      }),
      this.handleTargetsDialogClose,
    );
  };

  handleTargetsDialogClose = () => {
    this.setState({ targetsDialogOpen: false }, this.updateCounts);
  };

  hasFiltersChanged = () => {
    const { targetCountsRequest, exclusions } = this.state;
    const notificationRequest = this.getNotificationTargetsRequest();
    return !isEqual(
      {
        ...notificationRequest,
        exclusions,
      },
      targetCountsRequest,
    );
  };

  validationMessage = filter => {
    const { validationMessages } = this.state;
    const validation = validationMessages.find(vm => vm.name === filter.name);
    return (validation && validation.errors) || '';
  };

  clearAllValidations = () => {
    this.setState({ validationMessages: [] });
  };

  clearValidation = filter => {
    const { validationMessages } = this.state;
    this.setState({ validationMessages: validationMessages.filter(vm => vm.name !== filter.name) });
  };

  validateFilters(warnOnRepeat, warnOnFirst) {
    const { filters, receivers } = this.state;
    const { allReceivers, allFilters, intl, toRoute } = this.props;
    const validationMessages = [];

    filters.forEach(filter => {
      const filterValidationErrors = [];

      // 1. Validate that the filter.options array is not zero length
      if (filter.options.length === 0) {
        filterValidationErrors.push(
          <li key={`${filter.name}-no-options`}>
            {intl.formatMessage({
              id: 'notifications.send-form.validation.no-options',
              defaultMessage: 'The filter must have at least one option selected.',
            })}
          </li>,
        );
      }

      // 2. Validate that the filter is applicable to one of the selected receivers
      const allFilterInfo = (toRoute && [...(allFilters || [])]) || (allFilters || []);
      if (toRoute) {
        const exists = allFilterInfo.some(item => item.name === 'FROM_OVERVIEW');
        if (!exists) {
          allFilterInfo.push({
            name: 'FROM_OVERVIEW',
            applicable: allReceivers.filter(r => r.value !== 'ALL_STAFF'),
          });
        }
      }
      const applicableFilter = allFilterInfo.find(f => f.name === filter.name) || {};
      const applicableReceivers = applicableFilter.applicable || [];
      const applicableReceiverValues = applicableReceivers.map(receiver => receiver.value);
      const validReceivers = applicableReceivers
        .filter(receiver => (allReceivers || []).some(ar => ar.value === receiver.value))
        .map(receiver => receiver.label);

      const isApplicableToSelectedReceiver =
        applicableReceivers.length === 0 ||
        receivers.some(receiver => applicableReceiverValues.includes(receiver));

      if (!isApplicableToSelectedReceiver) {
        filterValidationErrors.push(
          <li key={`${filter.name}-not-applicable`}>
            {intl.formatMessage(
              {
                id: 'notifications.send-form.validation.not-applicable',
                defaultMessage: 'Only applicable to {validReceivers}.',
              },
              { validReceivers: validReceivers.join(', ') },
            )}
          </li>,
        );
      }

      if (filterValidationErrors.length > 0) {
        validationMessages.push({
          name: filter.name,
          errors: <ul>{filterValidationErrors}</ul>,
        });
      }
    });

    // alert the user if validation didnt change
    const repeat = warnOnRepeat && isEqual(this.state.validationMessages, validationMessages);
    if ((repeat || warnOnFirst) && validationMessages.length > 0) {
      this.setState({ validationMessages });
      this.props.showErrorNotification(
        intl.formatMessage({
          id: 'notifications.send-form.validation.fix-errors',
          defaultMessage: 'Please fix the validation errors to continue.',
        }),
      );
    } else {
      this.setState({ validationMessages });
    }

    // Return true if there are no validation errors
    return validationMessages.length === 0;
  }

  renderSendConfirmDialog = () => {
    const { confirmModalOpen, confirmModalVariant } = this.state;
    const { intl } = this.props;
    if (!confirmModalOpen) return null;
    const isCancel = confirmModalVariant === 'cancel';
    const { id, values } = this.getSendConfirmBodyKey(confirmModalVariant);
    return (
      <DestructiveConfirmationDialog
        open
        header={intl.formatMessage({
          id: isCancel ? 'diversion.send-confirm.title.cancel' : 'diversion.send-confirm.title',
        })}
        text={intl.formatMessage({ id }, values)}
        buttonYes={intl.formatMessage({
          id: isCancel
            ? 'diversion.send-confirm.button.confirm.cancel'
            : 'diversion.send-confirm.button.confirm',
        })}
        buttonNo={intl.formatMessage({ id: 'button.cancel' })}
        onConfirm={this.handleConfirmSend}
        onCancel={this.handleCancelSend}
      />
    );
  };

  renderTargetsDialog = () => {
    const { targetsDialogOpen, exclusions } = this.state;
    const { targets, targetsLoading } = this.props;

    return (
      targetsDialogOpen && (
        <Targets
          exclusions={exclusions}
          targets={targets}
          onSubmitExclusions={this.handleTargetsDialogSubmit}
          onClose={this.handleTargetsDialogClose}
          loading={targetsLoading}
        />
      )
    );
  };

  renderFilterDialog = () => {
    const { allFilters, intl } = this.props;
    const { filters } = this.state;

    return (
      <Dialog
        open={this.state.filterDialogOpen}
        onClose={this.handleFilterDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {intl.formatMessage({ id: 'notifications.send-form.dialog.filter.title' })}
        </DialogTitle>
        <DialogContent>
          {allFilters && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    {intl.formatMessage({ id: 'notifications.send-form.table.header.category' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'notifications.send-form.table.header.filter' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'notifications.send-form.table.header.description' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'notifications.send-form.table.header.applies-to' })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {differenceBy(allFilters, filters, 'name').map(filter => {
                  const hasOptions = Array.isArray(filter.options) && filter.options.length > 0;
                  const applicable =
                    (filter.applicable && filter.applicable.length > 0 && filter.applicable) ||
                    this.props.allReceivers;
                  return (
                    <TableRow
                      key={filter.name}
                      sx={theme => ({
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        ...(!hasOptions && {
                          pointerEvents: 'none',
                          opacity: 0.5,
                          cursor: 'default',
                        }),
                      })}
                      onClick={() => hasOptions && this.handleAddFilter(filter)}
                      hover={hasOptions}
                    >
                      <TableCell>{filter.category}</TableCell>
                      <TableCell>{filter.label}</TableCell>
                      <TableCell>
                        {filter.description}
                        {!hasOptions && (
                          <div className={styles.validationMessage}>
                            {intl.formatMessage({ id: 'notifications.send-form.table.no-options' })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{applicable.map(app => app.label).join(', ')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button labelId="button.cancel" onClick={this.handleFilterDialogClose} color="primary" />
        </DialogActions>
      </Dialog>
    );
  };

  renderRecipientsSummary = () => {
    const { allReceivers } = this.props;
    const { targetCounts } = this.state;
    return (
      // Plain table using the app's standard cell spacing. The old
      // `size="small"` + `padding="checkbox"` were a v1-era way to tighten these
      // label/count rows (checkbox padding on non-checkbox cells); under MUI 7
      // they just rendered the summary cramped, so they're dropped.
      <Table>
        {/* <TableHead> */}
        {/*   <TableRow> */}
        {/*     <TableCell padding={padding}>Recipient Type</TableCell> */}
        {/*     <TableCell padding={padding} align="right"> */}
        {/*       Count */}
        {/*     </TableCell> */}
        {/*   </TableRow> */}
        {/* </TableHead> */}
        <TableBody>
          {allReceivers.map(receiver => (
            <TableRow key={receiver.value}>
              <TableCell>{receiver.label}</TableCell>
              <TableCell align="right">
                {targetCounts && targetCounts.find(({ value }) => receiver.value === value).count}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  render() {
    const {
      type,
      receivers,
      filters,
      header,
      body,
      schedule,
      date,
      time,
      expiring,
      expDate,
      expTime,
      boardedOnly,
    } = this.state;

    const { allReceivers, allFilters, toRoute, routes, intl, userRoles } = this.props;

    if (!allReceivers) return <LoadableSplashScreen pastDelay />;

    const allFilterInfo = (toRoute && [...(allFilters || [])]) || (allFilters || []);
    if (toRoute) {
      const exists = allFilterInfo.some(item => item.name === 'FROM_OVERVIEW');

      if (!exists) {
        allFilterInfo.push({
          name: 'FROM_OVERVIEW',
          label: intl.formatMessage({
            id: 'notifications.send-form.dialog.filter.selected-routes',
          }),
          description: intl.formatMessage({
            id: 'notifications.send-form.dialog.filter.routes-from-overview',
          }),
          options: routes.map(route => ({
            label: route.number,
            value: route.id,
          })),
          applicable: allReceivers.filter(r => r.value !== 'ALL_STAFF'),
        });
      }
    }

    return (
      <div className="send-notice-form">
        <Box component="form" noValidate autoComplete="off" sx={{ p: 3 }}>
          <FormGroup row sx={{ pb: 3 }}>
            <Grid container spacing={0} sx={{ flexGrow: 1 }}>
              <Grid size={3}>
                <FormLabel component="legend">
                  {intl.formatMessage({ id: 'notifications.send-form.form.legend.recipients' })}
                </FormLabel>
                <FormGroup>
                  {allReceivers.map(receiver => (
                    <FormControlLabel
                      key={receiver.value}
                      control={
                        <Checkbox
                          checked={receivers.includes(receiver.value)}
                          onChange={this.handleReceiversChange}
                          value={receiver.value}
                          color="primary"
                        />
                      }
                      label={receiver.label}
                    />
                  ))}
                  {toRoute && (
                    <Tooltip
                      title={intl.formatMessage({
                        id: 'notifications.send-form.tooltip.boarded-only',
                      })}
                    >
                      <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                          <Checkbox
                            checked={boardedOnly}
                            onChange={this.handleCheckboxChange('boardedOnly')}
                            color="primary"
                          />
                        }
                        label={intl.formatMessage({
                          id: 'notifications.send-form.checkbox.boarded-only',
                        })}
                      />
                    </Tooltip>
                  )}
                </FormGroup>
              </Grid>

              <Grid sx={{ borderLeft: 1, borderColor: 'divider', px: 2 }} size={5}>
                <FormLabel component="legend">
                  {intl.formatMessage({
                    id: 'notifications.send-form.form.legend.additional-filtering',
                  })}
                </FormLabel>
                <List disablePadding dense sx={{ mb: 2 }}>
                  {filters.map(filter => (
                    <Filter
                      key={filter.name}
                      selectedOptions={filter.options}
                      filter={allFilterInfo.find(allFilter => allFilter.name === filter.name)}
                      onDelete={this.handleFilterDelete}
                      onOptionsChange={this.handleFilterOptionsChange}
                      alwaysShowModal={false}
                      validationMessage={this.validationMessage(filter)}
                    />
                  ))}
                </List>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  iconLeft={<AddIcon />}
                  labelId="notifications.send-form.button.add-filter"
                  sx={{ py: 0, pl: 2 }}
                  onClick={this.handleFilterDialogOpen}
                />
              </Grid>
              <Grid sx={{ borderLeft: 1, borderColor: 'divider', pl: 2 }} size={4}>
                <FormLabel component="legend">
                  {intl.formatMessage({
                    id: 'notifications.send-form.form.legend.recipients-and-exclusions',
                  })}
                </FormLabel>
                {this.renderRecipientsSummary()}
                <Box sx={{ mt: 1 }}>
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'notifications.send-form.tooltip.review-exclude',
                    })}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      iconLeft={<EditIcon />}
                      labelId="notifications.send-form.button.review-exclude"
                      sx={{ py: 0, pl: 2, mr: 0.5, mt: 0.5 }}
                      onClick={this.handleTargetsDialogOpen}
                    />
                  </Tooltip>
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'notifications.send-form.tooltip.refresh-summary',
                    })}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      iconLeft={<RefreshIcon />}
                      labelId="notifications.send-form.button.refresh-summary"
                      sx={{ py: 0, pl: 2, mt: 0.5 }}
                      onClick={this.handleRefreshTargets}
                      disabled={!this.hasFiltersChanged()}
                    />
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </FormGroup>
          <MessageEditor
            value={{
              header,
              body,
              type,
              diversion: this.state.diversion,
            }}
            onChange={this.handleMessageEditorChange}
            availableTypes={
              toRoute
                ? [
                    NOTICES.TYPES.GENERAL,
                    NOTICES.TYPES.DELAY,
                    NOTICES.TYPES.SECURITY,
                    NOTICES.TYPES.DIVERSION,
                  ]
                : [NOTICES.TYPES.GENERAL, NOTICES.TYPES.SECURITY, NOTICES.TYPES.DIVERSION]
            }
            userRoles={userRoles}
            validationErrors={{ diversion: this.state.diversionValidationErrors }}
            diversionNotificationId={this.state.diversionNotificationId}
          />
          <FormGroup row sx={{ pb: 3 }}>
            <Tooltip
              title={
                type === NOTICES.TYPES.DIVERSION
                  ? intl.formatMessage({ id: 'diversion.composer.scheduling.disabled-tooltip' })
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={schedule}
                    onChange={this.handleCheckboxChange('schedule')}
                    color="primary"
                    disabled={type === NOTICES.TYPES.DIVERSION}
                  />
                }
                label={intl.formatMessage({
                  id: 'notifications.send-form.checkbox.scheduled-message',
                })}
              />
            </Tooltip>
            {schedule && (
              <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <TextField
                  sx={{ mr: 1 }}
                  id="date"
                  label={intl.formatMessage({ id: 'notifications.send-form.input.date' })}
                  type="date"
                  value={date}
                  onChange={this.handleFormChange('date')}
                  margin="normal"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextField
                  id="time"
                  label={intl.formatMessage({ id: 'notifications.send-form.input.time' })}
                  type="time"
                  value={time}
                  onChange={this.handleFormChange('time')}
                  margin="normal"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            )}
            <Tooltip
              title={
                type === NOTICES.TYPES.DIVERSION
                  ? intl.formatMessage({ id: 'diversion.composer.scheduling.disabled-tooltip' })
                  : ''
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={expiring}
                    onChange={this.handleCheckboxChange('expiring')}
                    color="primary"
                    disabled={type === NOTICES.TYPES.DIVERSION}
                  />
                }
                label={intl.formatMessage({
                  id: 'notifications.send-form.checkbox.message-expires',
                })}
              />
            </Tooltip>
            {expiring && (
              <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                <TextField
                  sx={{ mr: 1 }}
                  id="expDate"
                  label={intl.formatMessage({ id: 'notifications.send-form.input.date' })}
                  type="date"
                  value={expDate}
                  onChange={this.handleFormChange('expDate')}
                  margin="normal"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
                <TextField
                  id="expTime"
                  label={intl.formatMessage({ id: 'notifications.send-form.input.time' })}
                  type="time"
                  value={expTime}
                  onChange={this.handleFormChange('expTime')}
                  margin="normal"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              </Box>
            )}
          </FormGroup>
        </Box>
        {/* Sticky-bottom send bar — the primary CTA must always be visible
            regardless of how much form content is above. Backed by a white
            background + top border so scrolled content doesn't bleed through. */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#ffffff',
            zIndex: 10,
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.04)',
          }}
        >
          <Button
            size="large"
            variant="contained"
            color="primary"
            labelId="dialog.notices.button.send"
            iconLeft={<SendIcon sx={{ mr: 0.5 }} />}
            disabled={this.isSubmitingDisabled()}
            onClick={this.handleSend}
          />
        </Box>
        {this.renderFilterDialog()}
        {this.renderTargetsDialog()}
        {this.renderSendConfirmDialog()}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  allReceivers: state.notices.receivers,
  allFilters: state.notices.filters,
  targets: state.notices.targets,
  targetsLoading: state.notices.targetsLoading,
  userRoles: (state.profile.user && state.profile.user.roles) || [],
});

const mapDispatchToProps = {
  createNotificationWithFilters,
  getNotificationReceivers,
  getNotificationFilterOptions,
  getNotificationTargets,
  showErrorNotification,
  clearNoticesReducer,
  focusDiversionsTabRequest,
};

// Named export for component tests (avoids need for redux-mock-store which isn't in
// package.json — verified) — intl-wrapped: this class reads `this.props.intl`
// throughout but is never itself wrapped in `injectIntl`. `util/IntlTestHelper`'s
// `renderWithIntl` wraps only in a real `IntlProvider` and nothing more, so without
// this wrapper `this.props.intl` would be `undefined` here. Production consumers use
// the default export below, which was already wrapped.
const IntlNotificationForm = injectIntl(NotificationForm);
export { IntlNotificationForm as NotificationForm };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(IntlNotificationForm);
